import QRCode from "qrcode";
import FBRLogo from "../../Images/fbrDigitalInvoicingLogo.jpg";
import {
  getLogoUrl,
  section236HAmountFor,
  section236HFinalAmountFor,
  section236HLabel,
  SECTION_236H_FINAL_LABEL,
  hasSection236H,
} from "../../global/Utils";
import { landlineForDisplay } from "../../global/landline";
import { mobileForInvoice } from "../../global/sellerPhones";
import { fmt, ratePct, hsCodeOf, loadLogo } from "./generateLogoInvoice";
import { toWords } from "number-to-words";
import moment from "moment";

// ─── "Thermal Invoice" pattern ──────────────────────────────────────────────
// A roll receipt for counter / POS printers, selected per company via
// Company.invoicePattern.
//
// Unlike the A4 patterns this one does not build a PDF. Cheap 80 mm drivers
// (POS-80 and friends) are ESC/POS raster drivers, and handing one a PDF with a
// custom page size and embedded fonts is what makes it feed a blank slip and
// print nothing - it cannot rasterise the page, so the job comes out empty.
// Printing HTML hands the driver a bitmap the browser has already rendered at
// the printer's own resolution, which is what these printers are built for, and
// it opens the print dialog directly instead of making the operator print a PDF
// out of a viewer.
//
// The receipt is written into an off-screen iframe and that iframe is printed,
// so nothing of the admin screen behind it ends up in the job.

// The page is declared at the paper width so it matches the size set in the
// driver. The content is a narrower column centred inside it, because the two
// kinds of 80 mm printer disagree about where their ~72 mm of printable width
// sits on the paper: one pins that band to the left edge (0-72 mm), another
// centres it (4-76 mm), and a driver handed a page wider than its band may
// centre the page image and crop ~4 mm off each side. Only 4-72 mm is printable
// under all three, so a centred column has to be 64 mm or less to survive them
// - at 64 mm there is 8 mm of slack on each side and nothing can be clipped.
//
// It is deliberately conservative: print `printer-width-test.html` from the
// project's docs folder on a given printer to find what that one really images,
// and widen this if there is room. A 58 mm printer needs 58 mm / 44 mm.
const PAPER_W = "80mm";
const PRINT_W = "64mm";

// The software vendor's credit, printed at the foot of every slip. Fixed text,
// not company data - it is the same whichever company the receipt is for.
const VENDOR = {
  name: "EBEACONSOFTS SMC PVT LTD",
  phone: "0305-4243913",
};

// A stalled image must not hold the print dialog shut for ever.
const IMAGE_TIMEOUT_MS = 6000;
// Not every browser fires afterprint; the frame is swept up regardless.
const CLEANUP_MS = 60000;

/** Everything interpolated into the receipt is company- or buyer-entered. */
const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * The receipt is written into an about:blank iframe, which inherits this page's
 * base URL - but only resolved URLs are worth relying on for an asset the job
 * cannot print without, so the bundled FBR mark is made absolute up front.
 */
const absolute = (url) => {
  try {
    return new URL(url, window.location.href).href;
  } catch (_) {
    return url;
  }
};

/** Whole units stay whole; fractional quantities keep two decimals. */
const qtyFmt = (q) => fmt(q, Number.isInteger(Number(q)) ? 0 : 2);

/** Label left, value hard right. Both wrap rather than run off the paper. */
const row = (label, value, cls = "") => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return `<div class="row ${cls}"><span class="k">${esc(label)}</span><span class="v">${esc(text)}</span></div>`;
};

/** A full-width line of text, dropped entirely when there is nothing to say. */
const line = (value, cls = "") => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return `<div class="${cls}">${esc(text)}</div>`;
};

const styles = `
  @page { size: ${PAPER_W} auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    width: ${PAPER_W};
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    /* A thermal head only prints black - keep the browser from thinning it. */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt {
    width: ${PRINT_W};
    margin: 0 auto;
    padding: 3mm 0 8mm;
    font-size: 7.5pt;
    line-height: 1.3;
  }
  .c { text-align: center; }
  .b { font-weight: 700; }
  .s { font-size: 6.5pt; }
  .xs { font-size: 6pt; }
  .name { font-size: 11pt; font-weight: 700; line-height: 1.2; }
  .title { font-size: 9.5pt; font-weight: 700; text-align: center; }
  .row { display: flex; justify-content: space-between; align-items: baseline; gap: 2mm; }
  .row .k { flex: 0 1 auto; }
  .row .v { flex: 1 1 auto; text-align: right; }
  /* A long unbroken code wraps instead of pushing the column off the edge. */
  .k, .v, .name, .wrap { overflow-wrap: anywhere; word-break: break-word; }
  .sub { padding-left: 2mm; }
  .item { margin-top: 1.5mm; }
  .hr { border-top: 1px dashed #000; margin: 1.5mm 0; }
  .hr2 { border-top: 1px solid #000; border-bottom: 1px solid #000; height: 1mm; margin: 1.5mm 0; }
  .total { font-size: 11pt; font-weight: 700; }
  .gap { height: 1.5mm; }
  img { display: block; }
  .logo { max-width: 42mm; max-height: 18mm; margin: 0 auto 1.5mm; }
  /* Wide enough that the wording inside the mark still reads at 203 dpi. */
  .fbr { width: 18mm; margin: 0 auto 1mm; }
  .qr { width: 30mm; height: 30mm; margin: 0 auto 1mm; }
`;

/** Builds the receipt markup. Pure - every asset arrives already resolved. */
const buildReceipt = ({ data, logo, qr }) => {
  const company = data?.companyId || {};
  const buyer = data?.buyerId || {};
  const items = data?.items || [];
  const invoiceRef = data?.FBRInvoiceRefNo || "";

  // ─── LETTERHEAD ──────────────────────────────────────────────────────────
  // A number the company does not have is left out, label and all.
  const mobile = mobileForInvoice(company?.CEOContact);
  const landline = landlineForDisplay(company?.CEOLandline);
  const contact = [
    mobile ? `Ph: ${mobile}` : "",
    landline ? `Landline: ${landline}` : "",
    company?.website || "",
  ]
    .filter(Boolean)
    .join("  |  ");

  // The sales tax registration number gets a line of its own at the head of the
  // slip, in the body size rather than the small one: it is the number a
  // customer or an inspector looks for first, and pairing it with the NTN on
  // one line buried both.
  const head = [
    logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : "",
    line(String(company?.companyName || "").toUpperCase(), "name c"),
    line(company?.businessAddress, "s c"),
    line(contact, "s c"),
    company?.salesTaxRegNo
      ? line(`STRN: ${company.salesTaxRegNo}`, "b c")
      : "",
    company?.ntn ? line(`NTN: ${company.ntn}`, "s c") : "",
    company?.posId ? line(`POS ID: ${company.posId}`, "s c") : "",
  ].join("");

  // ─── INVOICE META ────────────────────────────────────────────────────────
  // The invoice date is a plain date, so the counter time a customer expects on
  // a receipt comes from when the invoice was actually posted.
  const stamp = data?.invoicePostedAt || data?.createdAt;
  const meta = [
    row("Invoice #", data?.invoiceNumber, "b"),
    row("Date", data?.date ? moment(data.date).format("DD/MM/YYYY") : ""),
    row("Time", stamp ? moment(stamp).format("hh:mm A") : ""),
    row("Type", data?.invoiceType),
    row("D.O/D.C #", data?.billNo),
    row("PO #", data?.purchaseOrderNo),
  ].join("");

  // ─── BUYER ───────────────────────────────────────────────────────────────
  const bill =
    buyer?.businessName || buyer?.registrationNo
      ? [
          '<div class="hr"></div>',
          line("BILL TO", "s b"),
          line(String(buyer?.businessName || "").toUpperCase(), "b wrap"),
          line(buyer?.address, "s"),
          row("NTN/CNIC", buyer?.registrationNo, "s"),
          row("Sales Tax Reg. #", buyer?.saletax, "s"),
        ].join("")
      : "";

  // ─── ITEMS ───────────────────────────────────────────────────────────────
  const lines = items
    .map((item, i) => {
      const name = item?.itemId?.name || item?.productDescription || "";
      const hs = hsCodeOf(item);
      const qty = Number(item?.quantity) || 0;
      const unit = (Number(item?.valueSalesExcludingST) || 0) / (qty || 1);
      const rate = ratePct(item?.rate);

      // Per-line charges, each printed only when it actually applies, so a
      // plain taxable line stays two rows tall.
      const charges = [
        Number(item?.salesTaxApplicable)
          ? row(
              `Sales Tax${rate ? ` @ ${rate}` : ""}`,
              fmt(item.salesTaxApplicable, 2),
              "s sub",
            )
          : "",
        ...[
          ["Further Tax", item?.furtherTax],
          ["Extra Tax", item?.extraTax],
          ["FED Payable", item?.fedPayable],
          ["ST Withheld", item?.salesTaxWithheldAtSource],
        ].map(([label, value]) =>
          Number(value) ? row(label, fmt(value, 2), "s sub") : "",
        ),
        Number(item?.discount)
          ? row("Discount", `-${fmt(item.discount, 2)}`, "s sub")
          : "",
      ].join("");

      return [
        '<div class="item">',
        line(`${i + 1}. ${name}`, "b wrap"),
        hs ? line(`HS Code: ${hs}`, "xs") : "",
        row(
          `${qtyFmt(qty)}${item?.uoM ? ` ${item.uoM}` : ""} x ${fmt(unit, 2)}`,
          fmt(item?.valueSalesExcludingST, 2),
        ),
        charges,
        row("Line Total", fmt(item?.totalValues, 2), "b sub"),
        "</div>",
      ].join("");
    })
    .join("");

  // ─── TOTALS ──────────────────────────────────────────────────────────────
  const sum = (fn) => items.reduce((acc, it) => acc + (Number(fn(it)) || 0), 0);
  const totalIncl = data?.grandTotal ?? sum((it) => it?.totalValues);
  // Discount is deducted after the sales tax - the tax is charged on the
  // undiscounted value - so it only ever moves the inclusive total.
  const totalDiscount = sum((it) => it?.discount);
  const totalFurther = sum((it) => it?.furtherTax);
  const totalExtra = sum((it) => it?.extraTax);
  const totalFed = sum((it) => it?.fedPayable);

  const totals = [
    row("Items", String(items.length)),
    row("Total Qty", qtyFmt(sum((it) => it?.quantity))),
    row(
      "Excl. Sales Tax",
      fmt(data?.subTotal ?? sum((it) => it?.valueSalesExcludingST), 2),
    ),
    row(
      "Sales Tax",
      fmt(data?.taxTotal ?? sum((it) => it?.salesTaxApplicable), 2),
    ),
    totalFurther ? row("Further Tax", fmt(totalFurther, 2)) : "",
    totalExtra ? row("Extra Tax", fmt(totalExtra, 2)) : "",
    totalFed ? row("FED Payable", fmt(totalFed, 2)) : "",
    totalDiscount ? row("Less: Discount", `-${fmt(totalDiscount, 2)}`) : "",
  ].join("");

  // Section 236H and the final amount, printed after TOTAL PAYABLE. The 236H
  // amount is never part of the invoice value itself. Nothing is printed when
  // the invoice has no 236H rate.
  const section236H = hasSection236H(data?.GSection236H)
    ? [
        row(
          section236HLabel(data?.GSection236H),
          fmt(section236HAmountFor(totalIncl, data?.GSection236H), 2),
          "b",
        ),
        `<div class="row total"><span class="k">${esc(
          SECTION_236H_FINAL_LABEL.toUpperCase(),
        )}</span><span class="v">${esc(
          fmt(section236HFinalAmountFor(totalIncl, data?.GSection236H), 2),
        )}</span></div>`,
        '<div class="hr2"></div>',
      ].join("")
    : "";

  let words = "";
  try {
    words = `RUPEES ${toWords(
      Math.floor(Number(totalIncl) || 0),
    ).toUpperCase()} ONLY.`;
  } catch (_) {
    words = "";
  }

  // ─── FBR VERIFICATION ────────────────────────────────────────────────────
  // The mark and the QR only mean anything once FBR has accepted the invoice,
  // so the caller withholds the QR until then and this block goes with it.
  const fbr = qr
    ? [
        '<div class="hr"></div>',
        `<img class="fbr" src="${esc(absolute(FBRLogo))}" alt="" />`,
        `<img class="qr" src="${qr}" alt="" />`,
        line("FBR Invoice Number", "xs c"),
        line(invoiceRef, "b c wrap"),
        line("Scan to verify on the FBR portal", "xs c"),
      ].join("")
    : "";

  // ─── TERMS / FOOTER ──────────────────────────────────────────────────────
  const terms =
    company?.termsHeading || company?.termsText
      ? [
          '<div class="hr"></div>',
          line(String(company?.termsHeading || "").toUpperCase(), "s b"),
          line(company?.termsText, "xs"),
        ].join("")
      : "";

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(data?.invoiceNumber || "Invoice")}</title>
<style>${styles}</style>
</head>
<body>
<div class="receipt">
  ${head}
  <div class="hr2"></div>
  <div class="title">SALES TAX INVOICE</div>
  <div class="hr2"></div>
  ${meta}
  ${bill}
  <div class="hr"></div>
  <div class="row s b"><span class="k">ITEM / QTY x RATE</span><span class="v">AMOUNT</span></div>
  <div class="hr"></div>
  ${lines}
  <div class="hr"></div>
  ${totals}
  <div class="hr2"></div>
  <div class="row total"><span class="k">TOTAL PAYABLE</span><span class="v">${esc(fmt(totalIncl, 2))}</span></div>
  <div class="hr2"></div>
  ${section236H}
  ${line(words, "s b c")}
  ${fbr}
  ${terms}
  <div class="hr"></div>
  <div class="xs c">This is a computer generated invoice.</div>
  <div class="xs c">No signature is required.</div>
  <div class="gap"></div>
  <div class="b c">THANK YOU FOR YOUR VISIT</div>
  <div class="gap"></div>
  <div class="xs c">Software Developed By</div>
  <div class="s b c">${VENDOR.name}</div>
  <div class="xs c">PH: ${VENDOR.phone}</div>
</div>
</body>
</html>`;
};

/** Resolves once every image in the document has settled, or on timeout. */
const imagesReady = (doc) => {
  const pending = Array.from(doc.images || []).filter((img) => !img.complete);
  if (!pending.length) return Promise.resolve();
  return Promise.race([
    Promise.all(
      pending.map(
        (img) =>
          new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          }),
      ),
    ),
    new Promise((resolve) => setTimeout(resolve, IMAGE_TIMEOUT_MS)),
  ]);
};

/**
 * Prints the markup from an off-screen iframe. The frame is given a real size
 * rather than 0x0 - a collapsed frame lays out to nothing in some browsers and
 * prints blank - and is parked off-screen rather than hidden, since a frame
 * with `display: none` is never rendered and so has nothing to print.
 */
const printHtml = (html) =>
  new Promise((resolve) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("title", "Thermal invoice");
    frame.style.cssText =
      "position:fixed;left:-10000px;top:0;width:80mm;height:400mm;border:0;";
    document.body.appendChild(frame);

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      // Removal still races the dialog on some browsers, so the frame is left
      // in place briefly after the job has been handed over.
      setTimeout(() => frame.remove(), 1000);
      resolve();
    };

    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc) {
      frame.remove();
      resolve();
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    imagesReady(doc).then(() => {
      const win = frame.contentWindow;
      if (!win) return cleanup();
      win.onafterprint = cleanup;
      setTimeout(cleanup, CLEANUP_MS);
      try {
        win.focus();
        win.print();
      } catch (_) {
        cleanup();
      }
    });
  });

export const generateThermalInvoice = async (data) => {
  const invoiceRef = data?.FBRInvoiceRefNo || "";
  const fbrStatus =
    data?.FBRInvoiceStatus ||
    data?.FBRInvoiceStatusResponse?.validationResponse?.status ||
    "";
  const isFbrValid =
    String(fbrStatus).toLowerCase() === "valid" && Boolean(invoiceRef);

  const logo = await loadLogo(getLogoUrl(data?.companyId?.logo));
  const qr = isFbrValid
    ? await QRCode.toDataURL(invoiceRef, { width: 200, margin: 0 })
    : null;

  await printHtml(buildReceipt({ data, logo: logo?.dataUrl || null, qr }));
};
